import {Col, Row, Tabs} from 'antd';
import {CardProps} from 'antd/es/card';
import {FormProps} from 'antd/es/form';
import {useMasterAccountQuery} from 'api/generated';
import {PageHeader} from 'components';
import {WebhooksSettings} from 'components/WebhookSettings/WebhookSettings';
import {useTestModeState} from 'hooks/useTestModeState';
import React, {useEffect} from 'react';
import {RouteComponentProps, useHistory} from 'react-router-dom';
import {StringParam, useQueryParam} from 'use-query-params';
import {NotFound} from '../NotFound';
import {MasterAccountDefaults} from './AccountDefaults/MasterAccountDefaults';
import {MasterAccountAccounts} from './MasterAccountAccounts';
import {MasterAccountAnalytics} from './MasterAccountAnalytics';
import {MasterAccountDetails} from './Details/MasterAccountDetails';
import {MasterAccountPayments} from './MasterAccountPayments';
import {MasterAccountSettings} from './MasterAccountSettings';
import {MasterAccountTitle} from './MasterAccountTitle';
import {MasterAccountUsers} from './Users/MasterAccountUsers';
import {MasterAccountTimeline} from './Details/MasterAccountTimeline';

const {TabPane} = Tabs;

const narrowFormProps: FormProps = {
  labelAlign: 'left',
  labelCol: {span: 8},
  wrapperCol: {span: 16},
  colon: false
};

export const MasterAccount: React.FC<RouteComponentProps<{masterAccountId: string}>> = ({
  match
}) => {
  const {masterAccountId} = match.params;
  const history = useHistory();
  const [isTestMode] = useTestModeState();

  const {data, loading} = useMasterAccountQuery({
    variables: {id: masterAccountId}
  });
  const masterAccount = data?.masterAccount;
  const {test, testAccountId, prodAccountId} = masterAccount || {};

  const [_activeTab, setActiveTab] = useQueryParam('tab', StringParam);

  const activeTab = _activeTab || 'details';

  useEffect(() => {
    if (isTestMode && !test && testAccountId) {
      setTimeout(() => {
        history.push(`/master-accounts/${testAccountId}`);
      });
    }
    if (!isTestMode && test && prodAccountId) {
      setTimeout(() => {
        history.push(`/master-accounts/${prodAccountId}`);
      });
    }
  }, [isTestMode, history, test, testAccountId, prodAccountId]);

  type TabProps = {
    tab: string;
    key: string;
    children: JSX.Element;
    enabled: boolean;
  };

  const cardProps: CardProps = {
    size: 'small',
    loading,
    style: {
      marginBottom: 25
    }
  };

  const tabs: TabProps[] = [
    {
      tab: 'Details',
      key: 'details',
      children: (
        <Row gutter={20}>
          <Col span={16}>
            <MasterAccountDetails
              masterAccount={masterAccount}
              cardProps={cardProps}
              formProps={narrowFormProps}
            />
          </Col>
          <Col span={8}>
            <MasterAccountTimeline masterAccountId={masterAccountId} />
          </Col>
        </Row>
      ),
      enabled: true
    },
    {
      tab: 'Settings',
      key: 'settings',
      children: (
        <Row gutter={20}>
          <Col span={24}>
            <MasterAccountSettings
              masterAccount={masterAccount}
              cardProps={cardProps}
              formProps={narrowFormProps}
            />
          </Col>
        </Row>
      ),
      enabled: true
    },
    {
      tab: 'Account defaults',
      key: 'account-defaults',
      children: (
        <Row gutter={20}>
          <Col span={24}>
            <MasterAccountDefaults
              masterAccount={masterAccount}
              cardProps={cardProps}
              formProps={narrowFormProps}
            />
          </Col>
        </Row>
      ),
      enabled: true
    },
    {
      tab: 'Accounts',
      key: 'accounts',
      children: (
        <Row gutter={20}>
          <Col span={24}>
            <MasterAccountAccounts masterAccountId={masterAccountId} />
          </Col>
        </Row>
      ),
      enabled: true
    },
    {
      tab: 'Payments',
      key: 'payments',
      children: (
        <Row gutter={20}>
          <Col span={24}>
            <MasterAccountPayments masterAccountId={masterAccountId} />
          </Col>
        </Row>
      ),
      enabled: true
    },
    {
      tab: 'Analytics',
      key: 'analytics',
      children: (
        <Row gutter={20}>
          <Col span={24}>
            <MasterAccountAnalytics masterAccountId={masterAccountId} />
          </Col>
        </Row>
      ),
      enabled: true
    },
    {
      tab: 'Users',
      key: 'users',
      children: (
        <Row gutter={20}>
          <Col span={24}>
            <MasterAccountUsers masterAccountId={masterAccountId} cardProps={cardProps} />
          </Col>
        </Row>
      ),
      enabled: true
    },
    {
      tab: 'Webhooks',
      key: 'webhooks',
      children: (
        <Row gutter={20}>
          <Col span={24}>
            <WebhooksSettings
              accountId={masterAccountId}
              cardProps={cardProps}
              masterAccount={true}
            />
          </Col>
        </Row>
      ),
      enabled: true
    }
  ];

  if (!masterAccount && !loading) {
    return (
      <NotFound
        message={
          <>
            Sorry, master account with id <strong>{masterAccountId}</strong> does not exist
          </>
        }
      />
    );
  }

  return (
    <>
      <PageHeader loading={loading} backPath="/master-accounts">
        <MasterAccountTitle masterAccount={masterAccount} />
      </PageHeader>
      <Tabs activeKey={activeTab} onChange={(tab) => setActiveTab(tab)} type="card">
        {tabs.map((tab) =>
          tab.enabled ? (
            <TabPane tab={tab.tab} key={tab.key}>
              {tab.children}
            </TabPane>
          ) : (
            <></>
          )
        )}
      </Tabs>
    </>
  );
};
